/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { CTA, Text } from '../../../atoms';
import DropZone from '../../../atoms/DropZone';
import ButtonsBanner from '../ButtonsBanner';
import {
  COURSE_TAB_SAVE,
  TUTOR_DISABLED_TAB_SET,
} from '../../../../app/redux/actions/tutors';
import { acceptedDocsTypes } from '../../../../utils/fileExtension';
import { Legend } from './style';
import ModalInnerDocuments, {
  ModalInnerDocumentsId,
} from '../../../atoms/Modals/ModalInnerDocuments';
import DocumentsUploader from '../../DocumentsUploader';
import ModalCreateCourse, {
  ModalCreateCourseId,
} from '../../../atoms/Modals/ModalCreateCourse';
import { MODAL_OPEN } from '../../../../app/redux/actions';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import CardInnerDocument from '../../../atoms/Modals/ModalInnerDocuments/CardInnerDocument';
import { ToastError } from '../../Toast';
import CustomTooltip from '../../../atoms/CustomTooltip';

const Documents = ({
  mediaIsPhone,
  mediaIsTablet,
  isSupervisor,
  isAdmin,
  courseTabSave,
  activeTab,
  tabs,
  course,
  editDetails,
  setDisabledTab,
  modalOpen,
}) => {
  const { editMode } = course;
  const inputRef = useRef(null);
  const [validFormat, setValidFormat] = useState(true);
  const [importedDocuments, setImportedDocuments] = useState([]);
  const [documentsToUpload, setDocumentsToUpload] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState();

  useEffect(() => {
    if (editMode && editDetails) {
      setImportedDocuments(editDetails?.inner_documents);
      setDisabledTab({ 3: { completed: true } });
    } else {
      setDisabledTab({ 3: { completed: false } });
      setImportedDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  const onFileChange = (files = []) => {
    const newDocs = files?.filter(
      (f) => importedDocuments.findIndex((iDoc) => iDoc?.name === f?.name && iDoc?.size === f?.size) < 0
    );
    setImportedDocuments([...importedDocuments, ...newDocs]);
    setDocumentsToUpload([...documentsToUpload, ...newDocs]);
    if (inputRef) inputRef.current.value = null; //? clear input (otherwise onChange won't trigger)
    if (newDocs.length < files.length) {
      toast.error(
        <ToastError
          title="Attenzione"
          body="Uno o più file sono già stati importati"
        />
      );
    }
  };

  const onInnerDocsChange = (documents = []) => {
    const newDocs = documents?.filter(
      (d) => importedDocuments.findIndex((iDoc) => iDoc?.id === d?.id) < 0
    );
    setImportedDocuments([...importedDocuments, ...newDocs]);
    if (newDocs.length < documents.length) {
      toast.error(
        <ToastError
          title="Attenzione"
          body="Uno o più file sono già stati importati"
        />
      );
    }
  };

  const checkSupportedFiles = (files) => {
    let isSupported = true;
    files.forEach((f) => {
      if (acceptedDocsTypes.findIndex((t) => t === f.type) < 0) {
        isSupported = false;
      }
    });
    if (!isSupported) {
      setValidFormat(false);
    } else {
      onFileChange(files);
      setValidFormat(true);
    }
  };

  const handleFileDrop = (monitor) => {
    if (monitor) {
      const { files } = monitor.getItem();
      checkSupportedFiles(files);
    }
  };

  useEffect(() => {
    if (activeTab === 3) setDisabledTab({ 3: { completed: true } });
    return () => {
      if (activeTab === 3) {
        courseTabSave({
          tabName: 'documents',
          data: importedDocuments.filter((d) => d?.id).map((d) => d.id),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, importedDocuments]);

  return (
    <>
      <Text type="b3" mb={24}>
        Sezione dedicata al caricamento dei documenti relativi al percorso
        formativo che si sta creando.
      </Text>
      <Text type="h2" mb={8}>
        Inserisci documento
      </Text>
      {isSupervisor || isAdmin ? (
        <>
          {mediaIsPhone ? (
            <div className="d-flex flex-col align-start mb-5">
              <Text type="b4" mb={8}>
                Effettua la selezione da:
              </Text>
              <CTA
                btnText="Archivio SISFOR"
                fullsize
                onClick={() => modalOpen(ModalInnerDocumentsId)}
              />
              <CTA
                id="this-device"
                btnText="Questo dispositivo"
                fullsize
                onClick={() => inputRef.current.click()}
              />
              <input
                aria-labelledby="this-device"
                ref={inputRef}
                className="d-none"
                type="file"
                id="inputFile"
                onChange={(e) => onFileChange([e.target.files[0]])}
                accept={acceptedDocsTypes.join(',')}
              />
            </div>
          ) : (
            <DndProvider backend={HTML5Backend}>
              <DropZone
                onDrop={handleFileDrop}
                onClick={() => inputRef.current.click()}
                validFormat={validFormat}
                error={error}
              />
              <input
                aria-label="Questo dispositivo"
                ref={inputRef}
                className="d-none"
                type="file"
                id="inputFile"
                onChange={(e) => onFileChange([e.target.files[0]])}
                accept={acceptedDocsTypes.join(',')}
              />
            </DndProvider>
          )}
        </>
      ) : (
        <>
          <Text type="b4" mb={8}>
            Effettua la selezione da:
          </Text>
          <div className="d-flex mb-3">
            <CTA
              btnText="Archivio SISFOR"
              onClick={() => modalOpen(ModalInnerDocumentsId)}
              fullsize={mediaIsPhone}
            />
            <CustomTooltip
              type="primary"
              title="Upload file"
              tooltipKey="tutor_upload_file"
            />
          </div>
        </>
      )}
      <Text type="h2" mb={8}>
        Documenti importati
      </Text>
      <Text type="b3" mb={24}>
        {!importedDocuments?.length > 0
          ? 'Ancora nessun documento importato.'
          : `${importedDocuments?.length} documenti importati`}
      </Text>
      {importedDocuments?.length > 0 && (
        <>
          {!(mediaIsPhone || mediaIsTablet) && (
            <Legend className="uppercase">
              <Text type="h3.5">Formato</Text>
              <Text type="h3.5">Titolo</Text>
              <Text type="h3.5">Tipologia</Text>
              <Text type="h3.5">Data</Text>
              <Text type="h3.5">Dimensioni</Text>
            </Legend>
          )}
          {(importedDocuments || []).map((d, i) => (
            <CardInnerDocument
              key={`doc-${i}`}
              item={d}
              onClick={() => {
                if (!d?.id) {
                  const newDocsToUpload = documentsToUpload.filter(
                    (doc) => doc.name !== d?.name && doc.size !== d?.size
                  );
                  setDocumentsToUpload(newDocsToUpload);
                }
                const newDocs = [...importedDocuments];
                newDocs.splice(i, 1);
                setImportedDocuments(newDocs);
              }}
            />
          ))}
        </>
      )}
      <ButtonsBanner
        nextEnabled
        onClickConfirm={() => {
          if (tabs?.length === 3) modalOpen(ModalCreateCourseId);
        }}
      />
      <ModalInnerDocuments
        excluded_ids={(importedDocuments || [])
          .filter((d) => d?.id)
          .map((d) => d.id)}
        saveDocuments={(docs) => onInnerDocsChange(docs)}
      />
      <DocumentsUploader documents={documentsToUpload} />
      <ModalCreateCourse />
    </>
  );
};

Documents.propTypes = {
  mediaIsPhone: PropTypes.bool,
  mediaIsTablet: PropTypes.bool,
  isSupervisor: PropTypes.bool,
  isAdmin: PropTypes.bool,
  activeTab: PropTypes.number,
  tabs: PropTypes.array,
  course: PropTypes.object,
  editDetails: PropTypes.object,
  courseTabSave: PropTypes.func,
  setDisabledTab: PropTypes.func,
  modalOpen: PropTypes.func,
};

export default connect(
  (state) => {
    const { isSupervisor, isAdmin } = state.user;
    const { activeTab, course, tabs } = state.tutors;
    const { details: editDetails } = state.tutorCourseList;
    return {
      isSupervisor,
      isAdmin,
      activeTab,
      tabs,
      course,
      editDetails,
    };
  },
  (dispatch) => ({
    modalOpen: (id, payload = {}) =>
      dispatch({ type: MODAL_OPEN, id, payload }),
    setDisabledTab: (tabs) => dispatch({ type: TUTOR_DISABLED_TAB_SET, tabs }),
    courseTabSave: ({ tabName, data }) =>
      dispatch({ type: COURSE_TAB_SAVE, tabName, data }),
  })
)(withMediaQueries(Documents));
